(() => {
  if (window.fpDropdown) return;

  const instances = new WeakMap();
  const skip = 'select[data-fp-dropdown="off"],select.single-option-selector,select.product-single__variants,select[multiple]';
  let counter = 0;
  let openInstance = null;

  const textOf = option => (option.label || option.textContent || '').replace(/\s+/g, ' ').trim();
  const isPlaceholder = option => option.disabled && option.value === '';

  function labelFor(select) {
    if (select.id) {
      const linked = document.querySelector('label[for="' + select.id + '"]');
      if (linked) return linked;
    }
    return select.closest('label');
  }

  class Dropdown {
    constructor(select) {
      this.select = select;
      this.id = 'fp-dropdown-' + (++counter);
      this.active = -1;
      this.query = '';
      this.queryTimer = 0;
      this.build();
      instances.set(select, this);
      select._fpDropdown = this;
      select.addEventListener('change', () => this.sync());
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(select, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'selected', 'label', 'hidden'] });
      this.sync();
    }

    build() {
      const select = this.select;
      const root = document.createElement('div');
      root.className = 'fp-dropdown' + (select.dataset.fpDropdownClass ? ' ' + select.dataset.fpDropdownClass : '');
      const box = this.box = document.createElement('div');
      box.className = 'fp-dropdown__box';
      box.id = this.id;
      box.tabIndex = 0;
      box.setAttribute('role', 'combobox');
      box.setAttribute('aria-haspopup', 'listbox');
      box.setAttribute('aria-expanded', 'false');
      box.setAttribute('aria-controls', this.id + '-list');
      const value = this.valueNode = document.createElement('span');
      value.className = 'fp-dropdown__value';
      const icon = document.createElement('span');
      icon.className = 'fp-dropdown__icon';
      icon.setAttribute('aria-hidden', 'true');
      box.append(value, icon);
      const list = this.list = document.createElement('ul');
      list.className = 'fp-dropdown__list';
      list.id = this.id + '-list';
      list.setAttribute('role', 'listbox');
      list.tabIndex = -1;
      list.hidden = true;
      root.append(box, list);
      this.root = root;

      const label = labelFor(select);
      if (label) {
        if (!label.id) label.id = this.id + '-label';
        box.setAttribute('aria-labelledby', label.id);
        list.setAttribute('aria-labelledby', label.id);
        label.addEventListener('click', event => {
          if (event.target.closest('.fp-dropdown')) return;
          event.preventDefault();
          this.focus();
        });
      } else if (select.getAttribute('aria-labelledby')) {
        box.setAttribute('aria-labelledby', select.getAttribute('aria-labelledby'));
        list.setAttribute('aria-labelledby', select.getAttribute('aria-labelledby'));
      } else {
        const name = select.getAttribute('aria-label') || select.getAttribute('title') || document.body.dataset.chooseOption;
        box.setAttribute('aria-label', name);
        list.setAttribute('aria-label', name);
      }

      select.classList.add('fp-dropdown__native');
      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');
      select.parentNode.insertBefore(root, select.nextSibling);

      box.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
      box.addEventListener('keydown', event => this.keydown(event));
      list.addEventListener('pointerdown', event => event.preventDefault());
      list.addEventListener('click', event => {
        const item = event.target.closest('[role="option"]');
        if (!item) return;
        const index = Number(item.dataset.index);
        if (this.select.options[index].disabled) return;
        this.commit(index);
        this.close();
        this.box.focus();
      });
      list.addEventListener('pointermove', event => {
        const item = event.target.closest('[role="option"]');
        if (item && !item.hasAttribute('aria-disabled')) this.setActive(Number(item.dataset.index), false);
      });
    }

    get isOpen() { return !this.list.hidden; }

    options() { return Array.from(this.select.options).map((option, index) => ({ option, index })).filter(item => !isPlaceholder(item.option) && !item.option.hidden); }

    enabled() { return this.options().filter(item => !item.option.disabled); }

    sync() {
      const select = this.select;
      const selected = select.options[select.selectedIndex];
      this.list.textContent = '';
      for (const { option, index } of this.options()) {
        const item = document.createElement('li');
        item.className = 'fp-dropdown__option';
        item.id = this.id + '-option-' + index;
        item.dataset.index = index;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', option.selected ? 'true' : 'false');
        if (option.disabled) item.setAttribute('aria-disabled', 'true');
        if (option.lang) item.lang = option.lang;
        item.textContent = textOf(option);
        this.list.append(item);
      }
      this.valueNode.textContent = selected ? textOf(selected) : '';
      this.root.classList.toggle('is-placeholder', !selected || isPlaceholder(selected));
      this.root.classList.toggle('is-disabled', select.disabled);
      this.box.setAttribute('aria-disabled', select.disabled ? 'true' : 'false');
      this.box.tabIndex = select.disabled ? -1 : 0;
      this.root.hidden = select.hidden;
      if (this.isOpen) this.setActive(this.active, false); else this.active = -1;
    }

    setActive(index, scroll = true) {
      const previous = this.list.querySelector('.is-active');
      if (previous) previous.classList.remove('is-active');
      this.active = index;
      const item = this.list.querySelector('[data-index="' + index + '"]');
      if (!item) { this.box.removeAttribute('aria-activedescendant'); return; }
      item.classList.add('is-active');
      this.box.setAttribute('aria-activedescendant', item.id);
      if (scroll) item.scrollIntoView({ block: 'nearest' });
    }

    open(edge) {
      if (this.select.disabled || this.isOpen) return;
      if (openInstance && openInstance !== this) openInstance.close();
      openInstance = this;
      this.list.hidden = false;
      this.box.setAttribute('aria-expanded', 'true');
      this.root.classList.add('is-open');
      const rect = this.box.getBoundingClientRect();
      const height = Math.min(this.list.scrollHeight, 280);
      this.root.classList.toggle('is-above', rect.bottom + height > innerHeight - 8 && rect.top > height + 8);
      const enabled = this.enabled();
      let target = enabled.find(item => item.index === this.select.selectedIndex) || enabled[0];
      if (edge === 'last') target = enabled[enabled.length - 1];
      if (edge === 'first') target = enabled[0];
      this.setActive(target ? target.index : -1);
    }

    close() {
      if (!this.isOpen) return;
      this.list.hidden = true;
      this.box.setAttribute('aria-expanded', 'false');
      this.box.removeAttribute('aria-activedescendant');
      this.root.classList.remove('is-open', 'is-above');
      if (openInstance === this) openInstance = null;
    }

    focus() { this.box.focus(); }

    commit(index) {
      const select = this.select;
      if (select.selectedIndex === index) return;
      select.selectedIndex = index;
      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));
      this.sync();
    }

    move(delta) {
      const enabled = this.enabled();
      if (!enabled.length) return;
      const position = enabled.findIndex(item => item.index === this.active);
      const start = position < 0 ? (delta > 0 ? -1 : enabled.length) : position;
      this.setActive(enabled[Math.max(0, Math.min(enabled.length - 1, start + delta))].index);
    }

    typeahead(character) {
      clearTimeout(this.queryTimer);
      this.query += character.toLowerCase();
      this.queryTimer = setTimeout(() => { this.query = ''; }, 700);
      const enabled = this.enabled();
      const current = Math.max(0, enabled.findIndex(item => item.index === this.active));
      const offset = this.query.length > 1 ? current : current + 1;
      const match = enabled.slice(offset).concat(enabled.slice(0, offset)).find(item => textOf(item.option).toLowerCase().startsWith(this.query));
      if (!match) return;
      if (!this.isOpen) this.open();
      this.setActive(match.index);
    }

    keydown(event) {
      if (event.ctrlKey || event.metaKey) return;
      const key = event.key;
      if (!this.isOpen) {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' ', 'Home', 'End'].includes(key)) {
          event.preventDefault();
          this.open(key === 'End' ? 'last' : key === 'Home' ? 'first' : undefined);
        } else if (key.length === 1 && !event.altKey) this.typeahead(key);
        return;
      }
      switch (key) {
        case 'ArrowDown': event.preventDefault(); if (event.altKey) this.close(); else this.move(1); break;
        case 'ArrowUp':
          event.preventDefault();
          if (event.altKey) { if (this.active >= 0) this.commit(this.active); this.close(); } else this.move(-1);
          break;
        case 'Home': event.preventDefault(); this.setActive((this.enabled()[0] || {}).index); break;
        case 'End': event.preventDefault(); this.setActive((this.enabled().slice(-1)[0] || {}).index); break;
        case 'PageDown': event.preventDefault(); this.move(10); break;
        case 'PageUp': event.preventDefault(); this.move(-10); break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (key === ' ' && this.query) { this.typeahead(' '); break; }
          if (this.active >= 0) this.commit(this.active);
          this.close();
          break;
        case 'Escape': event.preventDefault(); event.stopPropagation(); this.close(); break;
        case 'Tab': this.close(); break;
        default: if (key.length === 1 && !event.altKey) this.typeahead(key);
      }
    }
  }

  function enhance(scope) {
    const root = scope && scope.querySelectorAll ? scope : document;
    const selects = root.matches && root.matches('select') ? [root] : Array.from(root.querySelectorAll('select'));
    for (const select of selects) {
      if (instances.has(select) || !select.parentNode) continue;
      if (select.matches(skip)) {
        if (select.matches('.single-option-selector,.product-single__variants')) { select.tabIndex = -1; select.setAttribute('aria-hidden', 'true'); }
        continue;
      }
      new Dropdown(select);
    }
  }

  function watch(proto, property, owner) {
    const descriptor = Object.getOwnPropertyDescriptor(proto, property);
    if (!descriptor || !descriptor.set) return;
    Object.defineProperty(proto, property, {
      configurable: true,
      enumerable: descriptor.enumerable,
      get: descriptor.get,
      set(next) {
        descriptor.set.call(this, next);
        const select = owner(this);
        const instance = select && instances.get(select);
        if (instance) instance.sync();
      }
    });
  }
  watch(HTMLSelectElement.prototype, 'value', select => select);
  watch(HTMLSelectElement.prototype, 'selectedIndex', select => select);
  watch(HTMLOptionElement.prototype, 'selected', option => option.parentNode && option.parentNode.closest ? option.parentNode.closest('select') : null);

  document.addEventListener('pointerdown', event => {
    if (openInstance && !openInstance.root.contains(event.target)) openInstance.close();
  }, true);
  document.addEventListener('focusin', event => {
    if (openInstance && !openInstance.root.contains(event.target)) openInstance.close();
  });

  window.fpDropdown = { enhance, get: select => instances.get(select) };

  const start = () => {
    enhance(document);
    new MutationObserver(records => {
      for (const record of records) for (const node of record.addedNodes) if (node.nodeType === 1) enhance(node);
    }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
